import sum from "lodash-es/sum";
import { JSX, useMemo } from "react";

const size = 108;
const strokeWidth = 2;
const strokeDashArray = [2, 4, 2, 0];
const spacing = 16;
const ringClassNames = [
  "stroke-theme-primary/10 animate-[spin_30s_linear_infinite] origin-center",
  "stroke-theme-primary/20 animate-[spin_25s_linear_infinite_reverse] origin-center",
  "stroke-theme-primary/40 animate-[spin_20s_linear_infinite] origin-center",
];

const rings = ringClassNames.map((className, i) => {
  const ring = {
    size: size - i * spacing,
    strokeDashArray: [...strokeDashArray],
  };

  const radius = ring.size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;

  // Scale the strokeDashArray to fit the desired number of occurrences
  {
    const length = sum(ring.strokeDashArray);
    const occurences = circumference / length;

    // Round the number of occurrences to the nearest multiple of 4, so that the pattern lines up nicely
    const desiredOccurences = Math.round(occurences / 4) * 4;

    const scale = occurences / desiredOccurences;

    ring.strokeDashArray =
      scale === 1
        ? ring.strokeDashArray
        : ring.strokeDashArray.map((v) => v * scale);
  }

  return {
    className,
    cx: "50%",
    cy: "50%",
    r: radius,
    strokeWidth: strokeWidth.toString(),
    strokeDasharray: ring.strokeDashArray.join(" "),
  };
});

export function DashedRings(props: JSX.IntrinsicElements["svg"]) {
  return (
    <svg
      {...props}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {rings.map((ring, i) => (
        <circle key={i} {...ring} />
      ))}
    </svg>
  );
}

export function DashedRing({
  className,
  size,
  strokeWidth,
  strokeDashArray,
}: {
  className: string;
  size: number;
  strokeWidth: number;
  strokeDashArray: number[];
}) {
  const radius = useMemo(() => size / 2 - strokeWidth / 2, [size, strokeWidth]);
  const circumference = useMemo(() => 2 * Math.PI * radius, [radius]);

  // Scale the strokeDashArray to fit the desired number of occurrences
  strokeDashArray = useMemo(() => {
    const length = sum(strokeDashArray);
    const occurences = circumference / length;

    // Round the number of occurrences to the nearest multiple of 4, so that the pattern lines up nicely
    const desiredOccurences = Math.round(occurences / 4) * 4;

    const scale = occurences / desiredOccurences;

    return scale === 1
      ? strokeDashArray
      : strokeDashArray.map((v) => v * scale);
  }, [circumference, strokeDashArray]);

  return (
    <circle
      className={className}
      cx="50%"
      cy="50%"
      r={radius}
      strokeWidth={strokeWidth}
      strokeDasharray={strokeDashArray.join(" ")}
    />
  );
}
